<template>
  <div class="card card-body mb-3">
    <div v-if="series">
      <apexchart
        type="rangeBar"
        height="350"
        :options="chartOptions"
        :series="series"
        ref="apexChart"
      />
      <div class="d-flex justify-content-between">
        <v-button
          size="xs"
          icon="chevron-left"
          :text="`${currentYear - 1}`"
          :translate-text="false"
          style-type="secondary"
          @click="() => toYear(currentYear - 1)"
        />

        <div class="text-muted">
          <v-button
            size="xs"
            :text="`${currentYear}`"
            :translate-text="false"
            style-type="link"
            @click="() => toYear(currentYear)"
          />
        </div>

        <v-button
          size="xs"
          icon="chevron-right"
          :text="`${currentYear + 1}`"
          :translate-text="false"
          style-type="secondary"
          @click="() => toYear(currentYear + 1)"
          :icon-right="true"
        />
      </div>
    </div>
    <div v-if="!relevantResearches.length" class="overlay centered">
      <span class="alert alert-warning">
        {{ $t('sentence.no_commercials_or_researches') }}
        <hr/>
        <v-button
          icon="plus"
          text="phrase.register_now"
          style-type="primary"
          @click="$router.push({ name: 'organisation-register-research-start' })"
        />
      </span>
    </div>
  </div>
</template>

<script>
import Button from '@/common/components/Button.vue';
import TimelineChartTooltip from '@/organisation/components/dashboard/TimelineChartTooltip.vue';
import { mapGetters } from 'vuex';
import Vue from 'vue';
import { CLICKABLE_STATUSES } from '@/organisation/services/researches';

export default {
  name: 'TimelineChart',
  components: {
    'v-button': Button,
  },
  props: {
    researches: {
      type: Array,
      required: true,
    },
  },
  data() {
    const component = this;
    return {
      currentYear: null,
      selectedResearchUuid: null,
      selected: {
        answersByResearchItem: null,
        research: null,
        researchItems: null,
        researchReviewSessions: null,
      },
      colors: [],
      timelineData: null,
      chartOptions: {
        chart: {
          height: 350,
          type: 'rangeBar',
          toolbar: {
            show: false,
          },
          events: {
            click(event, x, w) {
              const elem = w.config.series[w.seriesIndex];
              if (!elem) {
                return;
              }
              const data = elem.data[w.dataPointIndex];
              if (!data) {
                return;
              }
              if (CLICKABLE_STATUSES.includes(data.status)) {
                component.$router.push({
                  name: 'organisation-research-dashboard',
                  params: { uuid: data.uuid },
                });
              }
            },
          },
          zoom: {
            enabled: false,
          },
        },
        colors: null,
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
          },
        },
        tooltip: {
          custom({
            series,
            seriesIndex,
            dataPointIndex,
            w,
          }) {
            const data = w.config.series[seriesIndex].data[dataPointIndex];
            const ComponentConstructor = Vue.extend(TimelineChartTooltip);

            const componentInstance = new ComponentConstructor({
              propsData: {
                series,
                imageUrl: data.imageUrl,
                title: data.title,
                averageRating: data.averageRating,
                averageTotal: data.averageTotal,
                numberOfReviews: data.sessionsCount,
                startDate: data.startDate,
                endDate: data.endDate,
                status: data.status,
              },
            });
            const tempDiv = document.createElement('div');
            componentInstance.$mount(tempDiv);
            return componentInstance.$el.outerHTML;
          },
        },
        dataLabels: {
          enabled: true,
          formatter(val, w) {
            const data = w.config.series[0].data[w.dataPointIndex];
            if (data.averageRating) {
              return `${data.averageRating.toFixed(1)}`;
            }
            if (data.label) {
              return `${data.label}`;
            }
            return '';
          },
        },
        xaxis: {
          type: 'datetime',
          min: new Date().getTime(),
          max: new Date(new Date().getFullYear() + 1, 0, 1).getTime(),
        },
        legend: {
          position: 'top',
        },
      },
    };
  },
  methods: {
    toYear(year) {
      this.currentYear = year;
      this.$refs.apexChart.zoomX(
        new Date(this.currentYear, 0, 1).getTime(),
        new Date(this.currentYear + 1, 0, 1).getTime(),
      );
    },
    getTimelineData() {
      let colors = [];
      const tvObjects = this.getTimelineObjects('tv', 'word.tv_commercial');
      colors = colors.concat(new Array(tvObjects.length).fill('#A55A5E'));
      const radioObjects = this.getTimelineObjects('radio', 'word.radio_commercial');
      colors = colors.concat(new Array(radioObjects.length).fill('#175194'));
      const oohObjects = this.getTimelineObjects('ooh', 'word.ooh_commercial');
      colors = colors.concat(new Array(oohObjects.length).fill('#2e9417'));
      const researchObjects = this.getTimelineObjects(null, 'word.brand_research');
      colors = colors.concat(new Array(researchObjects.length).fill('#546E7A'));
      this.chartOptions.colors = colors;

      return [
        ...tvObjects,
        ...radioObjects,
        ...oohObjects,
        ...researchObjects,
      ];
    },
    getYearFromLatestResearchObject() {
      let currentYear = 0;
      this.timelineData.forEach((data) => {
        if (data.y[0] > currentYear) {
          // eslint-disable-next-line prefer-destructuring
          currentYear = data.y[0];
        }
      });
      return new Date(currentYear).getFullYear();
    },
    mapToChartObject(research, title) {
      const startDate = new Date(research.start_date);
      const endDate = new Date(research.end_date);

      const researchType = !research.commercial ? 'research' : research.commercial.commercial_type;
      let imageUrl = null;
      if (research.commercial) {
        if (research.commercial.commercial_type === 'tv') {
          imageUrl = research.commercial.youtube_image_url;
        } else if (research.commercial.commercial_type === 'radio') {
          // eslint-disable-next-line global-require
          imageUrl = require('@/common/assets/img/radio.png');
        } else if (research.commercial.commercial_type === 'ooh') {
          // eslint-disable-next-line global-require
          imageUrl = require('@/common/assets/img/billboard.png');
        }
      } else {
        // eslint-disable-next-line global-require
        imageUrl = require('@/common/assets/img/research.png');
      }

      return {
        x: this.$tc(title, 2),
        y: [startDate.getTime(), endDate.getTime()],
        uuid: research.uuid,
        researchType,
        title: research.title,
        status: research.status,
        imageUrl,
        averageRating: research.commercial ? research.commercial.average_rating : null,
        averageTotal: research.commercial ? research.average_total : null,
        startDate,
        endDate,
        sessionsCount: research.sessions_count,
      };
    },
    getTimelineObjects(commercialType, title) {
      let researchObjects;
      if (commercialType) {
        researchObjects = this.relevantResearches.filter(
          (research) => (
            research.commercial && research.commercial.commercial_type === commercialType
          ),
        );
      } else {
        researchObjects = this.relevantResearches.filter((research) => !research.commercial);
      }

      const chartObjects = researchObjects.map(
        (research) => this.mapToChartObject(research, title),
      );

      if (chartObjects.length > 0) {
        return chartObjects;
      }
      return [
        {
          x: this.$tc(title, 2),
          y: [],
        },
      ];
    },
  },
  computed: {
    ...mapGetters(['org']),
    relevantResearches() {
      return this.researches.filter((research) => (!!research.start_date && !!research.end_date));
    },
    series() {
      return [
        {
          data: this.timelineData,
        },
      ];
    },
  },

  created() {
    this.timelineData = this.getTimelineData();
  },
  mounted() {
    const toCurrentYear = () => {
      this.toYear(this.getYearFromLatestResearchObject());
    };
    setTimeout(() => {
      toCurrentYear();
    }, 100);
  },
};
</script>

<style scoped>
.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1050;
  background-color: rgba(255, 255, 255, 0.80);
}

.overlay-strong {
  background-color: rgba(255, 255, 255, 0.95);
}

.centered {
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
